/*eslint-disable */
const freeGlobal = typeof global === 'object' && global !== null && global.Object === Object && global;
const freeGlobalThis = typeof globalThis === 'object' && globalThis !== null && globalThis.Object === Object && globalThis;
const freeSelf = typeof self === 'object' && self !== null && self.Object === Object && self;
const root = freeGlobalThis || freeGlobal || freeSelf || Function('return this')();

function isObject(value) {
	const type = typeof value;
	return value != null && (type === 'object' || type === 'function');
}

export function debounce(func, wait, options) {
	let lastArgs;
	let lastThis;
	let maxWait;
	let result;
	let timerId;
	let lastCallTime;

	let lastInvokeTime = 0;
	let leading = false;
	let maxing = false;
	let trailing = true;

	const useRAF = !wait && wait !== 0 && typeof root.requestAnimationFrame === 'function';

	if (typeof func !== 'function') {
		throw new TypeError('Expected a function');
	}
	wait = +wait || 0;
	if (isObject(options)) {
		leading = !!options.leading;
		maxing = 'maxWait' in options;
		maxWait = maxing ? Math.max(+options.maxWait || 0, wait) : maxWait;
		trailing = 'trailing' in options ? !!options.trailing : trailing;
	}

	function invokeFunc(time) {
		const args = lastArgs;
		const thisArg = lastThis;

		lastArgs = lastThis = undefined;
		lastInvokeTime = time;
		result = func.apply(thisArg, args);
		return result;
	}

	function startTimer(pendingFunc, wait) {
		if (useRAF) {
			root.cancelAnimationFrame(timerId);
			return root.requestAnimationFrame(pendingFunc);
		}
		return setTimeout(pendingFunc, wait);
	}

	function cancelTimer(id) {
		if (useRAF) {
			return root.cancelAnimationFrame(id);
		}
		clearTimeout(id);
	}

	function leadingEdge(time) {
		lastInvokeTime = time;

		timerId = startTimer(timerExpired, wait);

		return leading ? invokeFunc(time) : result;
	}

	function remainingWait(time) {
		const timeSinceLastCall = time - lastCallTime;
		const timeSinceLastInvoke = time - lastInvokeTime;
		const timeWaiting = wait - timeSinceLastCall;

		return maxing ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke) : timeWaiting;
	}

	function shouldInvoke(time) {
		const timeSinceLastCall = time - lastCallTime;
		const timeSinceLastInvoke = time - lastInvokeTime;

		return lastCallTime === undefined || timeSinceLastCall >= wait || timeSinceLastCall < 0 || (maxing && timeSinceLastInvoke >= maxWait);
	}

	function timerExpired() {
		const time = Date.now();
		if (shouldInvoke(time)) {
			return trailingEdge(time);
		}

		timerId = startTimer(timerExpired, remainingWait(time));
	}

	function trailingEdge(time) {
		timerId = undefined;

		if (trailing && lastArgs) {
			return invokeFunc(time);
		}
		lastArgs = lastThis = undefined;
		return result;
	}

	function cancel() {
		if (timerId !== undefined) {
			cancelTimer(timerId);
		}
		lastInvokeTime = 0;
		lastArgs = lastCallTime = lastThis = timerId = undefined;
	}

	function flush() {
		return timerId === undefined ? result : trailingEdge(Date.now());
	}

	function pending() {
		return timerId !== undefined;
	}

	function debounced(...args) {
		const time = Date.now();
		const isInvoking = shouldInvoke(time);

		lastArgs = args;
		lastThis = this;
		lastCallTime = time;

		if (isInvoking) {
			if (timerId === undefined) {
				return leadingEdge(lastCallTime);
			}
			if (maxing) {
				timerId = startTimer(timerExpired, wait);
				return invokeFunc(lastCallTime);
			}
		}
		if (timerId === undefined) {
			timerId = startTimer(timerExpired, wait);
		}
		return result;
	}
	debounced.cancel = cancel;
	debounced.flush = flush;
	debounced.pending = pending;
	return debounced;
}

export function throttle(func, wait, options) {
	let leading = true;
	let trailing = true;

	if (typeof func !== 'function') {
		throw new TypeError('Expected a function');
	}
	if (isObject(options)) {
		leading = 'leading' in options ? !!options.leading : leading;
		trailing = 'trailing' in options ? !!options.trailing : trailing;
	}
	return debounce(func, wait, {
		leading,
		trailing,
		maxWait: wait,
	});
}
/* eslint-enable */
